import { assert, roundThousandths } from "@lib/utils/utils";
import sum from "lodash/sum";
import { z } from "zod";

const piecewisePointSchema = z.object({
  dollar: z.number(),
  value: z.number(),
  placedManually: z.boolean(),
});

export const mvfSchema = z.array(piecewisePointSchema);

export type MVF = z.infer<typeof mvfSchema>;
export type PiecewisePoint = z.infer<typeof piecewisePointSchema>;

export type MVFProfile = { [evaluableId: string]: MVF };

/** Maximum allowed utility value for MVF points. */
export const MAX_UTILITY = 1_000_000;

export type Allocations = {
  [evaluableId: string]: {
    amount: number;
    bottomPlaced: boolean;
  };
};

export type Tranche = {
  height: number;
  allocations: Allocations;
};

export type TrancheProfile = Tranche[];
export type RawMVFPoint = [number, number, boolean];
export type RawMVF = RawMVFPoint[];

export const typicalXIntercept = 1e5;

// parameter to eliminate points that define a y value negligibly different from the y value interpolated from surrounding points, to save on compute:
export const defaultYDecimals = 3;
export const defaultYPrecision = 1 / 10 ** defaultYDecimals;
export const defaultXDecimals = 0;
export const defaultXPrecision = typicalXIntercept / 1000;
const roundX = Math.round;
const roundY = roundThousandths;

export function mvfProfileFromTrancheProfile(
  trancheProfile: TrancheProfile,
): MVFProfile {
  const preMvfProfile: { [evaluableId: string]: RawMVF } = {};
  const totals: { [evaluableId: string]: number } = {};

  const trancheBottoms = getTrancheBottoms(trancheProfile);

  for (let i = 0; i < trancheProfile.length; i++) {
    const tranche = trancheProfile[i];
    const bottom = trancheBottoms[i];

    if (tranche == null || bottom == null) {
      continue;
    }

    for (const [recipient, { amount, bottomPlaced }] of Object.entries(
      tranche.allocations,
    )) {
      const recipientPoints =
        preMvfProfile[recipient] ?? (preMvfProfile[recipient] = []);
      const recipientTotal = totals[recipient] ?? 0;

      // I'm pretty sure this is no longer necessary, keeping it as a reminder
      // for now until we get everything else squared away. iirc this was
      // defensive in the case where extraneous y intercept tranches were being
      // generated.
      // if (!bottomPlaced && totals[recipient] === 0 && amount === 0) {
      //   continue;
      // }

      // If this is the first time we see a non-zero allocation, then create a
      // y-intercept point at the top of the tranche
      if (recipientTotal === 0) {
        recipientPoints.push([0, bottom + tranche.height, true]);
      }

      const updatedTotal = recipientTotal + amount;
      totals[recipient] = updatedTotal;
      recipientPoints.push([updatedTotal, bottom, bottomPlaced]);
    }
  }

  const mvfProfile: MVFProfile = {};

  for (const [recipient, points] of Object.entries(preMvfProfile)) {
    if (points.length === 0) {
      points.push([0, 0, true]);
    }

    const lastPoint = points[points.length - 1];

    if (lastPoint?.[1] !== 0) {
      points.push([totals[recipient] ?? 0, 0, false]);
    }

    mvfProfile[recipient] = makeMVF(points);
  }

  return mvfProfile;
}

export function tranchesFromMVFProfile(mvfProfile: MVFProfile): TrancheProfile {
  const yValues: number[] = [];
  const recipients: string[] = [];
  const tranches: Tranche[] = [];
  const totals: { [evaluableId: string]: number } = {};

  // sort the recipients by FDV in descending order of their corresponding MVFs
  const sortedEntries = Object.entries(mvfProfile).sort(([, a], [, b]) => {
    return (b[0]?.value ?? 0) - (a[0]?.value ?? 0);
  });

  for (const [recipient, mvf] of sortedEntries) {
    yValues.push(...mvf.map((point) => point.value));
    recipients.push(recipient);
    totals[recipient] = 0;
  }

  // Sort in descending order
  yValues.sort((a, b) => b - a);

  for (let i = 1; i < yValues.length; i++) {
    const top = yValues[i - 1];
    const bottom = yValues[i];

    if (top == null || bottom == null) {
      continue;
    }

    // performance optimization:
    // skip any duplicate points after the first two duplicates (two duplicates
    // could represent a zero height tranche, more than 2 represents the same
    // thing).
    if (i > 2 && top === bottom && top === yValues[i - 2]) {
      continue;
    }

    const allocations: Allocations = {};
    const deltaY = round(top - bottom, 3);
    // we only need to track if a tranche is empty to avoid adding tranches that
    // are empty AND have a deltaY of 0
    let isEmptyTranche = true;

    for (const recipient of recipients) {
      const mvf = mvfProfile[recipient];

      if (mvf == null || mvf.length === 0) {
        continue;
      }

      // single point 0 allocations need to be special cased, see comment at
      // function end
      if (isZeroMVF(mvf)) {
        continue;
      }

      // performance optimization:
      // since MVFs are sorted in descending order by FDV, we can break early if
      // the first time we see an MVF that has no points in the current tranche
      const firstPoint = mvf[0];

      if (firstPoint == null || bottom > firstPoint.value) {
        break;
      }

      // If deltaY is zero, then the MVF is flat and we want to get the dollars
      // of the last point on the flat section. If deltaY is not zero, then we
      // want to get the minimun dollars at that point in case the bottom point
      // is the start to a flat section
      const newDollarValue =
        deltaY === 0
          ? dollarMaxAtValue(mvf, bottom)
          : dollarMinAtValue(mvf, bottom);

      // round to avoid floating point errors
      // note: things will blow up if this rounds to 0
      const priorTotal = totals[recipient] ?? 0;
      const deltaX = round(newDollarValue - priorTotal, 3);

      // Often the intervals that we are looking at have no change.
      // Since we are just looking at every interval of all sorted Y values we
      // have no way of knowing until now if we are dealing with a horizontal
      // line or 2 points with the same value on different curves
      if (deltaX === 0 && deltaY === 0) {
        continue;
      }

      const { topPlaced, bottomPlaced } = getTopBottomPlaced(top, bottom, mvf);

      // Don't allocate to recipients until after the first non-zero allocation.
      // This prevent extraneous 0 allocations in the first tranche before the
      // y-intercept of a recipient
      if (!topPlaced && deltaX === 0) {
        continue;
      }

      // No automatically placed 0 allocations
      if (!bottomPlaced && deltaX === 0) {
        continue;
      }

      isEmptyTranche = false;

      allocations[recipient] = {
        amount: deltaX,
        bottomPlaced,
      };
      totals[recipient] = priorTotal + deltaX;
    }

    // if deltaY is 0 and the tranche is empty, then don't add it
    if (deltaY === 0 && isEmptyTranche) {
      continue;
    }

    tranches.push({ height: deltaY, allocations });
  }

  // special case for single point 0 allocations. This is required because 0
  // allocations are a single point and the above code looks at pairs of points
  // (intervals). I'm fairly certain that there is a mathy proof that we can't
  // recover a single point when looking at an interval
  const zeroAllocations: Allocations = {};
  let foundZeroAllocations = false;

  for (const recipient of recipients) {
    const mvf = mvfProfile[recipient];

    if (mvf == null || !isZeroMVF(mvf)) {
      continue;
    }

    foundZeroAllocations = true;
    zeroAllocations[recipient] = {
      amount: 0,
      bottomPlaced: true,
    };
  }

  if (foundZeroAllocations) {
    const lastTranche = tranches[tranches.length - 1];

    if (lastTranche == null || lastTranche.height !== 0) {
      tranches.push({ height: 0, allocations: {} });
    }

    const updatedLastTranche = tranches[tranches.length - 1];

    if (updatedLastTranche == null) {
      return tranches;
    }

    updatedLastTranche.allocations = {
      ...updatedLastTranche.allocations,
      ...zeroAllocations,
    };
  }
  // end special case

  return tranches;
}

export function isZeroMVF(mvf: MVF): boolean {
  return mvf.every((point) => point.value === 0);
}

function getTopBottomPlaced(
  top: number,
  bottom: number,
  mvf: MVF,
): {
  topPlaced: boolean;
  bottomPlaced: boolean;
} {
  let topPlaced = false;
  let bottomPlaced = false;

  for (const mvfPoint of mvf) {
    if (mvfPoint.value === top) {
      topPlaced = mvfPoint.placedManually;
    }

    if (mvfPoint.value === bottom) {
      bottomPlaced = mvfPoint.placedManually;
    }

    if (bottom > mvfPoint.value) {
      break;
    }
  }

  return {
    topPlaced,
    bottomPlaced,
  };
}

export function makeMVF(givenPoints: RawMVF): MVF {
  const nGiven = givenPoints.length;

  if (nGiven === 0) {
    throw new Error("MVF must contain at least one point");
  }

  const points = givenPoints.map(([dollar, value, placedManually]) => ({
    value: roundY(value),
    dollar: roundX(dollar),
    placedManually,
  }));
  const uniquePoints: typeof points = [];

  // This allows for an MVF with a single [0, 0] point which is used to
  // represent a zero evaluation. No other single point MVF is allowed.
  const firstPoint = points[0];
  const lastInputPoint = points[nGiven - 1];

  if (firstPoint == null || lastInputPoint == null) {
    throw new Error("MVF has missing boundary points");
  }

  assert(firstPoint.dollar === 0, "First point must have dollar 0");
  assert(lastInputPoint.value === 0, "Last point must have value 0");

  for (let i = 0; i < points.length - 1; i++) {
    const point = points[i];
    const nextPoint = points[i + 1];

    if (point == null || nextPoint == null) {
      continue;
    }

    // Make points unique
    if (
      !point.placedManually ||
      (point.value === nextPoint.value && point.dollar === nextPoint.dollar)
    ) {
      if (point.placedManually) {
        nextPoint.placedManually = true;
      }
    } else {
      uniquePoints.push(point);

      assert(0 <= point.dollar, "Dollars must be non-negative");
      assert(point.dollar <= nextPoint.dollar, "Dollars must be increasing");
      assert(
        nextPoint.value >= 0,
        () => `Value must be non-negative (got ${nextPoint.value})`,
      );
      assert(
        point.value >= nextPoint.value,
        () =>
          `Value must be non-increasing (got ${point.value} -> ${nextPoint.value})`,
      );
    }
  }

  const lastPoint = points[points.length - 1];

  if (lastPoint == null) {
    throw new Error("MVF has no final point");
  }

  uniquePoints.push(lastPoint);
  return uniquePoints;
}

export function dollarMaxAtValue(mvf: MVF, value: number): number {
  const i = mvf.findLastIndex((point) => point.value >= value);

  if (i === -1) {
    return 0;
  }

  const currentPoint = mvf[i];

  if (currentPoint == null) {
    return 0;
  }

  if (i === mvf.length - 1 || currentPoint.value === value) {
    return currentPoint.dollar;
  }

  const nextPoint = mvf[i + 1];

  if (nextPoint == null) {
    return currentPoint.dollar;
  }

  return interpolate({
    x: value,
    x0: currentPoint.value,
    x1: nextPoint.value,
    y0: currentPoint.dollar,
    y1: nextPoint.dollar,
  });
}

export function dollarMinAtValue(mvf: MVF, value: number): number {
  if (mvf.length === 0) {
    return 0;
  }

  const minLeftIx = mvf.findIndex((point) => point.value <= value);

  if (minLeftIx === -1) {
    const lastPoint = mvf[mvf.length - 1];
    return lastPoint?.dollar ?? 0;
  }

  const minLeftPoint = mvf[minLeftIx];

  if (minLeftPoint == null) {
    return 0;
  }

  if (minLeftIx === 0) {
    return minLeftPoint.dollar;
  }

  const previousPoint = mvf[minLeftIx - 1];

  if (previousPoint == null) {
    return minLeftPoint.dollar;
  }

  return interpolate({
    x: value,
    x0: previousPoint.value,
    x1: minLeftPoint.value,
    y0: previousPoint.dollar,
    y1: minLeftPoint.dollar,
  });
}

export function getTrancheBottoms(trancheProfile: TrancheProfile): number[] {
  let runningSum = 0;
  const bottoms: number[] = [];

  for (const tranche of trancheProfile.toReversed()) {
    bottoms.push(runningSum);
    runningSum += tranche.height;
  }

  return bottoms.reverse();
}

/**
 * Return the minimum budget needed for the unilateral allocation process to
 * reach a given marginal value.
 *
 * Values above the tranche profile's maximum return 0.
 * Values below 0 are invalid and throw.
 */
export function budgetFromValue(
  trancheProfile: TrancheProfile,
  value: number,
): number {
  if (value < 0) {
    throw new Error("Value must be non-negative");
  }

  if (trancheProfile.length === 0) {
    return 0;
  }

  const trancheBottoms = getTrancheBottoms(trancheProfile);
  const maxValue = (trancheBottoms[0] ?? 0) + (trancheProfile[0]?.height ?? 0);

  if (value > maxValue) {
    return 0;
  }

  const epsilon = 1e-9;
  let budgetAbove = 0;

  for (let i = 0; i < trancheProfile.length; i++) {
    const tranche = trancheProfile[i];
    const bottom = trancheBottoms[i];

    if (tranche == null || bottom == null) {
      continue;
    }

    const top = bottom + tranche.height;
    const trancheBudget = sum(
      Object.values(tranche.allocations).map((allocation) => allocation.amount),
    );

    if (value <= top + epsilon && value >= bottom - epsilon) {
      if (Math.abs(tranche.height) <= epsilon) {
        return budgetAbove;
      }

      const fractionOfTrancheSpent = (top - value) / tranche.height;
      return budgetAbove + fractionOfTrancheSpent * trancheBudget;
    }

    budgetAbove += trancheBudget;
  }

  return budgetAbove;
}

// Return number rounded to ndigits precision after the decimal point.
export function round(n: number, ndigits: number): number {
  return Math.round(n * 10 ** ndigits) / 10 ** ndigits;
}

export function interpolate({
  x,
  x0,
  x1,
  y0,
  y1,
}: {
  x: number;
  x0: number;
  x1: number;
  y0: number;
  y1: number;
}): number {
  return y0 + ((x - x0) * (y1 - y0)) / (x1 - x0);
}
